import { useEffect, useState } from "react";
import SNAPSHOT from "../data/gpu-prices.json";
import { validatePrices, type GpuPrices } from "./llmVram";

// Prix de l'heure : l'instantané du build sert au rendu serveur et à l'hydratation (même
// HTML des deux côtés), puis le fichier relevé chaque heure le remplace s'il est valide. Toute
// donnée invalide ou absente laisse l'instantané en place : la page ne casse jamais.

export const NO_PRICES: GpuPrices = { updated: "1970-01-01T00:00:00Z", providers: {} };

export function usePrices() {
  const [prices, setPrices] = useState<GpuPrices>(() => validatePrices(SNAPSHOT) ?? NO_PRICES);
  const [checked, setChecked] = useState(false);
  // Heure du relevé de ce fichier, notée quand il arrive : le rendu reste pur (pas de Date.now()).
  const [checkedAt, setCheckedAt] = useState(0);
  useEffect(() => {
    fetch("/data/gpu-prices.json", { cache: "no-cache" })
      .then((res) => (res.ok ? res.json() : null))
      .then((d: unknown) => { const valid = validatePrices(d); if (valid) setPrices(valid); })
      .catch(() => { /* hors ligne ou pas encore généré : l'instantané reste affiché */ })
      .finally(() => { setChecked(true); setCheckedAt(Date.now()); });
  }, []);
  // Ancienneté calculée côté client seulement : l'heure diffère entre serveur et navigateur.
  // Relevé chaque heure : au-delà de 6 h sans relevé, les prix sont signalés comme anciens.
  const stale = checked && checkedAt - Date.parse(prices.updated) > 6 * 36e5;
  return { prices, checked, stale };
}

/** « 2026-10-08 16:05 UTC » : date et heure du relevé (les prix changent dans la journée).
 *  En UTC, tirée de la chaîne ISO : le même texte côté serveur et navigateur. */
export const priceStamp = (iso: string) => `${iso.slice(0, 10)} ${iso.slice(11, 16)} UTC`;

/** Charge un JSON du dossier /data et le valide ; null tant qu'il n'est pas là ou s'il est invalide. */
export function useDataFile<T>(url: string | null, validate: (d: unknown) => T | null) {
  const [state, setState] = useState<{ url: string | null; data: T | null; status: "loading" | "ok" | "error" }>({ url, data: null, status: "loading" });
  useEffect(() => {
    if (!url) return;
    let alive = true;
    fetch(url, { cache: "no-cache" })
      .then((res) => (res.ok ? res.json() : null))
      .then((d: unknown) => { const v = validate(d); if (alive) setState({ url, data: v, status: v ? "ok" : "error" }); })
      .catch(() => { if (alive) setState({ url, data: null, status: "error" }); });
    return () => { alive = false; };
  }, [url, validate]);
  // Navigation vers un autre GPU : l'ancien résultat ne doit pas s'afficher sous le nouveau titre.
  return state.url === url ? state : { url, data: null, status: "loading" as const };
}

/** Fichier de la veille (/data/…) : l'instantané du build d'abord, pour un rendu serveur et une
 *  hydratation identiques, puis la version du jour si elle est valide. `validate` doit être
 *  stable (fonction de module). */
export function useLiveData<T>(url: string, snapshot: unknown, validate: (d: unknown) => T | null): T | null {
  const [data, setData] = useState<T | null>(() => validate(snapshot));
  useEffect(() => {
    let alive = true;
    fetch(url, { cache: "no-cache" })
      .then((res) => (res.ok ? res.json() : null))
      .then((d: unknown) => { const v = validate(d); if (alive && v) setData(v); })
      .catch(() => { /* hors ligne ou pas encore généré : l'instantané reste affiché */ });
    return () => { alive = false; };
  }, [url, validate]);
  return data;
}
